import { cn, IconButton } from '@bridebook/aura';
import { ChevronLeft, Close } from '@bridebook/aura/src/icons';
import { useModalControlContext } from '../modal-control-context';
import { useRoundedModalContainerContext } from './rounded-modal-container-context';
import { useIsScrolledContext } from '../is-scrolled-context';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  ComponentProps,
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  HTMLAttributes,
} from 'react';
import { cva, VariantProps } from 'class-variance-authority';

type RoundedModalHeaderProps = HTMLAttributes<HTMLDivElement> & {
  showCloseButton?: boolean;
  closeButtonProps?: ComponentPropsWithoutRef<typeof RoundedModalCloseButton>;
  onBackButtonClick?: () => void;
  backButtonProps?: ComponentPropsWithoutRef<typeof IconButton>;
  /**
   * Variant of the modal's header.
   * No need to set it. It is set automatically via context based on the size of the modal.
   * You can override it in some edge cases if needed.
   * - default: default layout buttons higher, and header in the lower center
   * - horizontal: layout with back button, header and close button in one row
   */
  variant?: 'default' | 'horizontal';
};

export const RoundedModalHeader = ({
  className,
  children,
  showCloseButton = true,
  closeButtonProps,
  onBackButtonClick,
  backButtonProps,
  variant: variantProp,
  ...props
}: RoundedModalHeaderProps) => {
  const { isContentScrolled } = useIsScrolledContext();
  const { width } = useRoundedModalContainerContext();
  const variant = variantProp || (['lg'].includes(width) ? 'horizontal' : 'default');

  return (
    <div
      data-slot="rounded-modal-header"
      data-scrolled={isContentScrolled}
      className={cn(
        'px-6 pt-6 pb-3',
        'border-b border-b-transparent transition-colors data-[scrolled=true]:border-space-15',
        className,
      )}
      {...props}>
      {/* GRID - changes the layout based on the variant*/}
      <div className="grid grid-cols-[1fr_auto_1fr] gap-x-3">
        {/* BACK BUTTON or left button can be overridden with backButtonProps */}
        <div className="col-span-1 col-start-1 row-start-1 flex justify-start">
          {onBackButtonClick && (
            <IconButton
              size="sm"
              variant="grey"
              icon={ChevronLeft}
              onClick={onBackButtonClick}
              {...backButtonProps}
            />
          )}
        </div>
        {/* CLOSE BUTTON or right button can be overridden with closeButtonProps */}
        <div className="col-span-1 col-start-3 row-start-1 flex justify-end">
          {showCloseButton && <RoundedModalCloseButton {...closeButtonProps} />}
        </div>
        {/* CHILDREN */}
        <div
          className={cn(
            'flex flex-col items-center justify-center gap-4',
            // default: children is in second row
            variant === 'default' && 'col-span-3 col-start-1 row-start-2',
            // horizontal: children is in first row in the center
            variant === 'horizontal' && 'col-span-1 col-start-2 row-start-1',
          )}>
          {children}
        </div>
      </div>
    </div>
  );
};

/**
 *************************************************************
 */

const roundedModalTitleVariants = cva('', {
  variants: {
    variant: {
      coupleside: 'display-2xl',
      b2b: 'typo-2xl-semibold',
    },
  },
});

type RoundedModalTitleProps = ComponentProps<typeof DialogPrimitive.Title> & {
  variant?: NonNullable<VariantProps<typeof roundedModalTitleVariants>['variant']>;
};

export const RoundedModalTitle = ({
  className,
  children,
  variant = 'coupleside',
  ...props
}: RoundedModalTitleProps) => (
  <DialogPrimitive.Title
    data-slot="rounded-modal-title"
    className={cn(
      'relative m-0 text-center text-space',
      roundedModalTitleVariants({ variant }),
      className,
    )}
    {...props}>
    {children}
  </DialogPrimitive.Title>
);

export const RoundedModalCloseButton = forwardRef<
  ElementRef<typeof IconButton>,
  Omit<ComponentPropsWithoutRef<typeof IconButton>, 'icon'>
>(({ className, ...props }, ref) => {
  const { close } = useModalControlContext();
  return (
    <IconButton
      variant="grey"
      size="sm"
      icon={Close}
      onClick={close}
      ref={ref}
      className={className}
      {...props}
    />
  );
});

RoundedModalCloseButton.displayName = 'RoundedModalCloseButton';
